import {
  AlertColor,
  Box,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import Notification from 'components/Notification';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Config from 'utils/Config';
import { pastNoOfDaysList } from 'utils/constants/data';
import { identifiers } from 'utils/constants/identifiers';
import { fetchFromStorage } from 'utils/storage';
import strings from '../../../common/Translation/Translate';
import axiosInstance from '../../../utils/axios';
import { privileges } from '../../../utils/constants/privileges';
import { URLS } from '../../../utils/constants/urls';
import { Privilege } from '../../../utils/redux/reducer/authentication-slice';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';
import { useLazyLoadWhenVisible } from 'utils/hooks/useLazyLoadWhenVisible';
import { pageTitleSx } from 'utils/styles/pageHeader';
import {
  downloadExportFile,
  getBookingTypes,
} from '../../../utils/validators/HelperFunctions';
import BirthdaysAnniversaries from './BirthdaysAnniversaries';
import BookingsSummaryCard from './BookingsSummaryCard';
import DashboardAlertsAndActions from './DashboardAlertsAndActions';
import DashboardChartsSection from './DashboardChartsSection';
import RfqBooking from './RfqBooking';
import PastBookingDetailsDrawer from './PastBookingDetailsModal';
import RevenueOverview from './RevenueOverview';
import TopPerformersCard from './TopPerformersCard';
import UpComingBeo from './UpComingBeo';
import UpComingBooking from './UpComingBooking';
import type { YearsRevenueMode } from '../Reports/indianFinancialYear';

const YEARS_REVENUE_MODE_STORAGE_KEY = 'dashboard_years_revenue_mode';

function readYearsRevenueMode(): YearsRevenueMode {
  try {
    const raw = localStorage.getItem(YEARS_REVENUE_MODE_STORAGE_KEY);
    if (raw === 'CALENDAR' || raw === 'FISCAL') return raw;
  } catch {
    /* ignore */
  }
  return 'FISCAL';
}

function eventOverlapsLocalToday(startMs: number, endMs: number) {
  const dayStart = moment().startOf('day');
  const dayEnd = moment().endOf('day');
  const s = moment(startMs);
  const e = moment(endMs);
  return s.isSameOrBefore(dayEnd) && e.isSameOrAfter(dayStart);
}

const Dashboard: FC = () => {
  const theme = useTheme();
  const privilegesData = useSelector(Privilege);
  const [pastBooking, setPastBooking] = useState<{ bookingCounts?: any[] }>({ bookingCounts: [] });
  const [pastBookingDetails, setPastBookingDetails] = useState<any[]>([]);
  const [pastBookingDetailsLoading, setPastBookingDetailsLoading] = useState(false);
  const [revenueOverview, setRevenueOverview] = useState<any>(null);
  const [isRevenueLoading, setIsRevenueLoading] = useState<boolean>(true);
  const [yearsRevenueMode, setYearsRevenueMode] = useState<YearsRevenueMode>(() => readYearsRevenueMode());

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalHeaderText, setModalHeaderText] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [upComingBooking, setUpComingBooking] = useState<any>([]);
  const [tentativeBooking, setTentativeBooking] = useState<any>([]);
  const [waitlistedBooking, setWaitlistedBooking] = useState<any>([]);
  const [upcomingBEOs, setUpcomingBEOs] = useState<any>([]);
  const [rfqs, setRfqs] = useState<any>([]);
  const [cancelRequest, setCancelRequest] = useState<any>([]);
  const [editRequest, setEditRequest] = useState<any>([]);
  const [noOfPastDays, setNoOfPastDays] = useState<string>('7');
  const pastNoOfDaysOptions = pastNoOfDaysList(strings);
  const [noOfDaysUpcoming, setNoOfDaysUpcoming] = useState<string>('7');
  const [noOfDaysUpcomingBEO, setNoOfDaysUpcomingBEO] = useState<string>('7');
  const [noOfDaysTentative, setNoOfDaysTentative] = useState<string>('7');
  const [noOfDaysWaitlisted, setNoOfDaysWaitlisted] = useState<string>('7');
  const [birthdaysAnniversaries, setBirthdaysAnniversaries] = useState<any>([]);

  const [isLoading, setIsLoading] = useState({
    past: true,
    upcoming: true,
    tentative: true,
    waitlisted: false,
    beos: false,
    rfqs: false,
    cancelRequests: true,
    editRequests: true,
    birthdaysAnniversaries: false,
  });

  const handleChange = (event: SelectChangeEvent) => {
    setNoOfPastDays(event.target.value);
  };
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = useCallback(
    (error: any) => {
      if (error.response) {
        if (error?.response?.status === 403) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.forbidden,
          });
        } else {
          setMessage({
            display: true,
            severity: 'error',
            message: error.response.data?.error_description ?? error.response.data.message,
          });
        }
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    },
    [setMessage],
  );

  const fetchRevenueOverview = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.dashboard_revenue_overview, {
        params: { yearMode: yearsRevenueMode },
      });
      setRevenueOverview(data);
    } catch (e) {
      handleCatch(e);
    } finally {
      setIsRevenueLoading(false);
    }
  }, [handleCatch, yearsRevenueMode]);

  const handleYearsRevenueModeChange = useCallback((mode: YearsRevenueMode) => {
    setYearsRevenueMode(mode);
    try {
      localStorage.setItem(YEARS_REVENUE_MODE_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchPastBookings = useCallback(async () => {
    setIsLoading(prev => ({ ...prev, past: true }));
    try {
      const body = { params: { daysToSubtract: noOfPastDays } };
      const { data } = await axiosInstance.get(URLS.dashboard_past_bookings, body);
      setPastBooking({ bookingCounts: data?.bookingCounts ?? [] });
    } catch (error) {
      handleCatch(error);
    } finally {
      setIsLoading(prev => ({ ...prev, past: false }));
    }
  }, [noOfPastDays, handleCatch]);

  const fetchPastBookingDetails = useCallback(
    async (status: string) => {
      setPastBookingDetailsLoading(true);
      try {
        const { data } = await axiosInstance.get(URLS.dashboard_past_bookings_details, {
          params: { daysToSubtract: noOfPastDays, status },
        });
        setPastBookingDetails(Array.isArray(data) ? data : []);
      } catch (error) {
        handleCatch(error);
        setPastBookingDetails([]);
      } finally {
        setPastBookingDetailsLoading(false);
      }
    },
    [noOfPastDays, handleCatch],
  );

  const formatINR = (value: number) =>
    formatCurrencyWithSymbol(value);

  const createdDateFilterLabel =
    pastNoOfDaysOptions.find((o: any) => String(o.value) === String(noOfPastDays))?.label ?? '';

  const createdDateFilterLabelFriendly = (() => {
    const label = String(createdDateFilterLabel || '').trim();
    const pastDaysMatch = label.match(/^Past\s+(\d+)\s+days$/i);
    if (pastDaysMatch) return `Last ${pastDaysMatch[1]} days`;
    if (/^today$/i.test(label)) return 'Today';
    return label;
  })();

  useEffect(() => {
    fetchPastBookings();
  }, [fetchPastBookings]);

  useEffect(() => {
    fetchRevenueOverview();
  }, [fetchRevenueOverview]);

  const fetchUpcomingBookings = useCallback(async () => {
    setIsLoading(prev => ({ ...prev, upcoming: true }));
    try {
      const body = { params: { daysToAdd: noOfDaysUpcoming } };
      const { data } = await axiosInstance.get(URLS.dashboard_upcoming_bookings, body);
      setUpComingBooking(data);
    } catch (e) {
      handleCatch(e);
    } finally {
      setIsLoading(prev => ({ ...prev, upcoming: false }));
    }
  }, [noOfDaysUpcoming]);

  useEffect(() => {
    fetchUpcomingBookings();
  }, [fetchUpcomingBookings]);

  const fetchTentativeBookings = useCallback(async () => {
    setIsLoading(prev => ({ ...prev, tentative: true }));
    try {
      const body = { params: { daysToAdd: noOfDaysTentative } };
      const { data } = await axiosInstance.get(URLS.dashboard_cancel_tantative_bookings, body);
      setTentativeBooking(data);
    } catch (e) {
      handleCatch(e);
    } finally {
      setIsLoading(prev => ({ ...prev, tentative: false }));
    }
  }, [noOfDaysTentative]);

  useEffect(() => {
    fetchTentativeBookings();
  }, [fetchTentativeBookings]);

  const fetchWaitlistedBookings = useCallback(async () => {
    setIsLoading(prev => ({ ...prev, waitlisted: true }));
    try {
      const body = { params: { daysToAdd: noOfDaysWaitlisted } };
      const { data } = await axiosInstance.get(URLS.dashboard_waitlisted_bookings, body);
      setWaitlistedBooking(data);
    } catch (e) {
      handleCatch(e);
    } finally {
      setIsLoading(prev => ({ ...prev, waitlisted: false }));
    }
  }, [noOfDaysWaitlisted, handleCatch]);

  const { ref: waitlistedSectionRef, hasBeenVisible: waitlistedSectionVisible } = useLazyLoadWhenVisible();
  const { ref: rfqSectionRef, hasBeenVisible: rfqSectionVisible } = useLazyLoadWhenVisible();
  const { ref: beoSectionRef, hasBeenVisible: beoSectionVisible } = useLazyLoadWhenVisible();
  const { ref: birthdaysSectionRef, hasBeenVisible: birthdaysSectionVisible } = useLazyLoadWhenVisible();
  const waitlistedFetchEnabledRef = useRef(false);
  const beoFetchEnabledRef = useRef(false);

  useEffect(() => {
    if (waitlistedSectionVisible) {
      waitlistedFetchEnabledRef.current = true;
    }
  }, [waitlistedSectionVisible]);

  useEffect(() => {
    if (waitlistedFetchEnabledRef.current) {
      fetchWaitlistedBookings();
    }
  }, [waitlistedSectionVisible, noOfDaysWaitlisted, fetchWaitlistedBookings]);

  const fetchUpcomingBEOs = useCallback(async () => {
    setIsLoading(prev => ({ ...prev, beos: true }));
    try {
      const body = { params: { daysToAdd: noOfDaysUpcomingBEO } };
      const { data } = await axiosInstance.get(URLS.dashboard_upcoming_beos, body);
      setUpcomingBEOs(data);
    } catch (e) {
      handleCatch(e);
    } finally {
      setIsLoading(prev => ({ ...prev, beos: false }));
    }
  }, [noOfDaysUpcomingBEO, handleCatch]);

  const fetchBirthdaysAnniversaries = useCallback(async () => {
    setIsLoading(prev => ({ ...prev, birthdaysAnniversaries: true }));
    try {
      const { data, status } = await axiosInstance.get(URLS.dashboard_birthdays_anniversaries);
      if (status === 200) {
        setBirthdaysAnniversaries(data || []);
      }
    } catch (e) {
      handleCatch(e);
    } finally {
      setIsLoading(prev => ({ ...prev, birthdaysAnniversaries: false }));
    }
  }, [handleCatch]);

  useEffect(() => {
    if (beoSectionVisible) {
      beoFetchEnabledRef.current = true;
    }
  }, [beoSectionVisible]);

  useEffect(() => {
    if (beoFetchEnabledRef.current) {
      fetchUpcomingBEOs();
    }
  }, [beoSectionVisible, noOfDaysUpcomingBEO, fetchUpcomingBEOs]);

  useEffect(() => {
    if (birthdaysSectionVisible) {
      fetchBirthdaysAnniversaries();
    }
  }, [birthdaysSectionVisible, fetchBirthdaysAnniversaries]);

  const fetchRfqs = useCallback(async () => {
    setIsLoading(prev => ({ ...prev, rfqs: true }));
    try {
      const { data } = await axiosInstance.get(URLS.dashboard_rfq_bookings);
      setRfqs(data);
    } catch (e) {
      handleCatch(e);
    } finally {
      setIsLoading(prev => ({ ...prev, rfqs: false }));
    }
  }, [handleCatch]);

  useEffect(() => {
    if (rfqSectionVisible && privilegesData?.includes(privileges.view_rfq)) {
      fetchRfqs();
    }
  }, [rfqSectionVisible, fetchRfqs, privilegesData]);

  const fetchBookingRequests = useCallback(async () => {
    setIsLoading(prev => ({ ...prev, cancelRequests: true, editRequests: true }));
    try {
      const { data, status } = await axiosInstance.get(URLS.dashboard_booking_requests);
      if (status === 200) {
        setCancelRequest(data?.cancelRequests ?? []);
        setEditRequest(data?.editRequests ?? []);
      }
    } catch (e) {
      handleCatch(e);
    } finally {
      setIsLoading(prev => ({ ...prev, cancelRequests: false, editRequests: false }));
    }
  }, [handleCatch]);

  useEffect(() => {
    fetchBookingRequests();
  }, [fetchBookingRequests]);

  const handleExport = () => {
    if (upComingBooking?.length === 0) {
      setMessage({
        display: true,
        severity: 'info',
        message: strings.no_bookings_available_for_the_day,
      });
    } else {
      const token = fetchFromStorage(identifiers.access_token) as any;
      axios({
        url: `${Config.BaseURL + URLS.exportBookingsOfDay}`,
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        params: { daysToAdd: noOfDaysUpcoming },
        responseType: 'blob',
      })
        .then(response => {
          downloadExportFile(response, 'xlsx', `Bookings`);
        })
        .catch(err => {
          if (err?.response?.status === 403) {
            setMessage({
              display: true,
              severity: 'error',
              message: strings.forbidden,
            });
          }
        });
    }
  };

  const openOrderInfo = (name: string) => {
    setOpenModal(true);
    setSelectedStatus(name);
    setModalHeaderText(getBookingTypes(name));
    setPastBookingDetails([]);
    fetchPastBookingDetails(name);
  };

  const screenSizeUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const screenSizeUpSm = useMediaQuery(theme.breakpoints.up('sm'));

  const selectWidth = () => {
    if (screenSizeUpMd) return '18vw';
    else if (screenSizeUpSm) {
      return '30vw';
    } else return '90vw';
  };

  const pastBookingModalSubtitle = useMemo(() => {
    const filterLabel = createdDateFilterLabelFriendly;
    const stats = pastBooking?.bookingCounts?.find((c: any) => c.name === selectedStatus);
    const count = stats?.value ?? 0;
    const amount = typeof stats?.amount === 'number' ? stats.amount : null;
    const amountLabel = selectedStatus === 'PAYMENT_DUE' ? 'pending' : 'revenue';
    const countLabel = count === 1 ? 'booking' : 'bookings';
    const parts = [`Created ${filterLabel.toLowerCase()} · ${count} ${countLabel}`];
    if (amount != null) parts.push(`${formatINR(amount)} ${amountLabel}`);
    return parts.join(' · ');
  }, [createdDateFilterLabelFriendly, pastBooking?.bookingCounts, selectedStatus]);

  const showLoader = (
    <Box display="flex" alignItems="center" justifyContent="center" height={200} width="100%">
      <CircularProgress />
    </Box>
  );

  const operationsQuickMetrics = useMemo(() => {
    const bookings = Array.isArray(upComingBooking) ? upComingBooking : [];
    const beos = Array.isArray(upcomingBEOs) ? upcomingBEOs : [];

    const eventsToday = bookings.filter((b: any) =>
      typeof b?.startDate === 'number' && typeof b?.endDate === 'number'
        ? eventOverlapsLocalToday(b.startDate, b.endDate)
        : false,
    ).length;

    const beoPendingToday = beos.filter(
      (b: any) =>
        typeof b?.startDate === 'number' &&
        typeof b?.endDate === 'number' &&
        eventOverlapsLocalToday(b.startDate, b.endDate),
    ).length;

    return { eventsToday, beoPendingToday };
  }, [upComingBooking, upcomingBEOs]);

  const dashboardAlertsAndToday = useMemo(() => {
    const bookings = Array.isArray(upComingBooking) ? upComingBooking : [];
    const beoIds = new Set((Array.isArray(upcomingBEOs) ? upcomingBEOs : []).map((b: any) => b.lineItemId));
    const eventsTodayList = bookings.filter(
      (b: any) =>
        typeof b?.startDate === 'number' &&
        typeof b?.endDate === 'number' &&
        eventOverlapsLocalToday(b.startDate, b.endDate),
    );
    const eventsScheduled = eventsTodayList.length;
    const eventsTodayWithoutBeo = eventsTodayList.filter((b: any) => !beoIds.has(b.lineItemId)).length;

    const pastCounts = pastBooking?.bookingCounts;
    const pay = Array.isArray(pastCounts) ? pastCounts.find((c: any) => c.name === 'PAYMENT_DUE') : null;
    const paymentDueCount = typeof pay?.value === 'number' ? pay.value : 0;
    const paymentDueAmount = typeof pay?.amount === 'number' ? pay.amount : 0;

    const tentative = Array.isArray(tentativeBooking) ? tentativeBooking : [];
    const tentativeCount = tentative.length;
    const tentativePotentialTotal = tentative.reduce(
      (acc: number, t: any) => acc + (typeof t?.revenue === 'number' ? t.revenue : 0),
      0,
    );

    const beoPendingToday = (Array.isArray(upcomingBEOs) ? upcomingBEOs : []).filter(
      (b: any) =>
        typeof b?.startDate === 'number' &&
        typeof b?.endDate === 'number' &&
        eventOverlapsLocalToday(b.startDate, b.endDate),
    ).length;

    return {
      alerts: {
        eventsTodayWithoutBeo,
        paymentDueCount,
        paymentDueAmount,
        tentativeCount,
        tentativePotentialTotal,
      },
      today: {
        eventsScheduled,
        beoPending: beoPendingToday,
        paymentsToCollect: paymentDueCount,
        tentativeToConvert: tentativeCount,
      },
    };
  }, [upComingBooking, upcomingBEOs, pastBooking, tentativeBooking]);

  const insightsPanelLoading = isLoading.upcoming || isLoading.tentative || isLoading.past;

  const requestItems = () => {
    const arr = [];

    privilegesData?.includes(privileges.view_booking) &&
      arr.push(
        {
          key: 'cancel',
          type: 'cancel',
          title: strings.dashboard_Request_for_cancel,
          pageResponse: cancelRequest,
          getList: fetchBookingRequests,
          loading: isLoading.cancelRequests,
          showDivider: true,
        },
        {
          key: 'edit',
          type: 'edit',
          title: strings.dashboard_Request_for_edit,
          pageResponse: editRequest,
          getList: fetchBookingRequests,
          loading: isLoading.editRequests,
        },
      );

    return arr;
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={5000}
        />
      )}

      <Container maxWidth={false} sx={{ px: { xs: 0, sm: 0 }, mb: 3 }}>
        <Grid container spacing={2} sx={{ px: { xs: 1, sm: 2 } }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ ...pageTitleSx, fontWeight: 600 }}>{strings.dashboard_title_text}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} mt={0.5}>
            <RevenueOverview
              data={revenueOverview ?? undefined}
              isLoading={isRevenueLoading}
              yearMode={yearsRevenueMode}
              onYearModeChange={handleYearsRevenueModeChange}
            />
          </Grid>
          <Grid item xs={12}>
            <DashboardAlertsAndActions
              isLoading={insightsPanelLoading}
              alerts={dashboardAlertsAndToday.alerts}
              today={dashboardAlertsAndToday.today}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
                <TopPerformersCard
                  isLoading={isRevenueLoading}
                  topVenuesMtd={revenueOverview?.topVenuesMtd ?? []}
                  topEventTypesMtd={revenueOverview?.topEventTypesMtd ?? []}
                />
              </Grid>
              <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
                {privilegesData?.includes(privileges.view_booking) && (
                  <DashboardChartsSection onError={handleCatch} />
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                gap: 1,
                flexWrap: 'wrap',
              }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 800,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                  }}>
                  Bookings Summary
                </Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: 'text.secondary', mt: 0.25 }}>
                  Bookings created: {createdDateFilterLabelFriendly}
                </Typography>
              </Box>
              <FormControl
                size="small"
                sx={{
                  width: selectWidth(),
                  '& .MuiSelect-select': { height: '15px', lineHeight: '25px' },
                }}>
                <Select
                  value={noOfPastDays}
                  onChange={handleChange}
                  sx={{
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                  }}>
                  {pastNoOfDaysOptions.map((item: any) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 1 }}>
              <BookingsSummaryCard
                bookingCounts={pastBooking.bookingCounts || []}
                isLoading={isLoading.past}
                onRowClick={openOrderInfo}
                hideRfq={!privilegesData?.includes(privileges.view_rfq)}
              />
            </Box>
          </Grid>
          <PastBookingDetailsDrawer
            open={openModal}
            onClose={() => setOpenModal(false)}
            title={modalHeaderText}
            subtitle={pastBookingModalSubtitle}
            selectedStatus={selectedStatus}
            loading={pastBookingDetailsLoading}
            rows={pastBookingDetails}
          />
          {/* Bookings Section */}

          {[
            {
              data: upComingBooking,
              loading: isLoading.upcoming,
              days: noOfDaysUpcoming,
              setDays: setNoOfDaysUpcoming,
              title: strings.dashboard_text_upcoming_booking,
              exportable: true,
            },
            {
              data: tentativeBooking,
              loading: isLoading.tentative,
              days: noOfDaysTentative,
              setDays: setNoOfDaysTentative,
              title: strings.dashboard_text_tentative_booking,
            },
          ].map((props, idx) => (
            <Grid key={idx} my={1} item xs={12} md={6} sx={{ pl: { xs: 0, sm: idx % 2 === 0 ? 0 : 2 } }}>
              {props.loading ? (
                showLoader
              ) : (
                <UpComingBooking
                  title={props.title}
                  upComingBooking={props.data}
                  noOfDays={props.days}
                  setNoOfDays={props.setDays}
                  isExportable={props.exportable}
                  handleExport={handleExport}
                  maxBodyHeight={280}
                />
              )}
            </Grid>
          ))}
          <Grid
            ref={waitlistedSectionRef}
            my={1}
            item
            xs={12}
            md={6}
            sx={{ pl: { xs: 0, sm: 2 }, minHeight: 1 }}>
            {waitlistedSectionVisible &&
              (isLoading.waitlisted ? (
                showLoader
              ) : (
                <UpComingBooking
                  title={strings.dashboard_text_waitlisted_booking}
                  upComingBooking={waitlistedBooking}
                  noOfDays={noOfDaysWaitlisted}
                  setNoOfDays={setNoOfDaysWaitlisted}
                  maxBodyHeight={280}
                />
              ))}
          </Grid>
          {privilegesData?.includes(privileges.view_rfq) && (
            <Grid ref={rfqSectionRef} item xs={12} md={6} my={1} sx={{ pl: { xs: 0, sm: '14px' }, minHeight: 1 }}>
              {rfqSectionVisible &&
                (isLoading.rfqs ? (
                  showLoader
                ) : (
                  <RfqBooking
                    type="rfq"
                    title={strings.dashboard_Request_for_quote}
                    pageResponse={rfqs}
                    getList={fetchRfqs}
                    editPrivileges={privilegesData?.includes(privileges.view_booking)}
                    maxBodyHeight={280}
                  />
                ))}
            </Grid>
          )}
          {/* Requests */}
          {requestItems()
            .filter(req => req.pageResponse?.length > 0)
            .map((req, idx) => {
              const filteredItems = requestItems().filter(r => r.pageResponse?.length > 0);
              const isFirstInRow = idx === 0 || (idx > 0 && filteredItems[idx - 1]?.showDivider);
              return (
                <React.Fragment key={req.key}>
                  {req.showDivider && (
                    <Grid item sm={12} my={0.5}>
                      <Divider
                        sx={{
                          width: '100%',
                          border: `1px dashed ${theme.palette.divider}`,
                        }}
                      />
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={12}
                    md={6}
                    my={1}
                    sx={{
                      pl: { xs: 0, sm: isFirstInRow ? 0 : 2 },
                    }}>
                    {req.loading ? (
                      showLoader
                    ) : (
                      <RfqBooking
                        type={req.type}
                        title={req.title}
                        pageResponse={req.pageResponse}
                        getList={req.getList}
                        editPrivileges={privilegesData?.includes(privileges.view_booking)}
                        maxBodyHeight={280}
                      />
                    )}
                  </Grid>
                </React.Fragment>
              );
            })}
          {/* Operations & Approvals: BEO (primary) + Credit approvals (secondary) */}
          <Grid item xs={12} my={1} ref={beoSectionRef}>
            <Divider sx={{ width: '100%', border: `1px dashed ${theme.palette.divider}` }} />
          </Grid>
          <Grid item xs={12} sx={{ mt: 0.5, mb: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.2 }}>Operations & Approvals</Typography>
            <Box
              sx={{
                mt: 1.25,
                mb: 0.5,
                px: 2,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                background: 'linear-gradient(90deg, rgba(239, 246, 255, 0.9) 0%, rgba(250, 245, 255, 0.75) 100%)',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
              }}>
              <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 800, color: 'text.secondary' }}>
                Today:
              </Typography>
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                {operationsQuickMetrics.eventsToday} {operationsQuickMetrics.eventsToday === 1 ? 'Event' : 'Events'}
              </Typography>
              <Typography component="span" sx={{ fontSize: 13, color: 'text.disabled', fontWeight: 700 }}>
                |
              </Typography>
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                {operationsQuickMetrics.beoPendingToday} {operationsQuickMetrics.beoPendingToday === 1 ? 'BEO' : 'BEOs'}{' '}
                pending
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ mb: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {beoSectionVisible && (isLoading.beos ? (
                  showLoader
                ) : (
                  <UpComingBeo
                    title="Upcoming BEOs (Next Week)"
                    UpComingBeo={upcomingBEOs}
                    noOfDays={noOfDaysUpcomingBEO}
                    setNoOfDays={setNoOfDaysUpcomingBEO}
                    maxBodyHeight={280}
                  />
                ))}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} ref={birthdaysSectionRef} sx={{ minHeight: 1 }}>
            {birthdaysSectionVisible &&
              (isLoading.birthdaysAnniversaries
                ? showLoader
                : birthdaysAnniversaries.length > 0 && (
                    <>
                      <Divider sx={{ width: '100%', border: `1px dashed ${theme.palette.divider}`, my: 1 }} />
                      <BirthdaysAnniversaries
                        title="This Month's Birthdays & Anniversaries"
                        data={birthdaysAnniversaries}
                      />
                    </>
                  ))}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Dashboard;
